import '@babel/polyfill';
import 'mutationobserver-shim';
import Vue from 'vue';
import Vuelidate from 'vuelidate';
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue';
import 'bootstrap-vue/dist/bootstrap-vue-icons.min.css';
import RoleMixin from './mixins/RoleMixin.vue';
import UtilsMixin from './mixins/UtilsMixin.vue';

import App from './App.vue';
import router from './router';
import store from './store';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';

// Import Bootstrap an BootstrapVue CSS files (order is important)
import '@/scss/index.scss';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import { parseJwt } from './utils/helpers';
import VueSanitize from 'vue-sanitize';
import { userManager, setUser } from './utils/auth';
import userAPI from '@/api/user';
import {
  sanitizeHybridCoursesBreadcrumb,
  sanitizeHybridCoursesSentryEvent,
  shouldCreateHybridCoursesSpanForRequest,
} from './utils/hybrid-courses-privacy';

Vue.use(Vuelidate);
Vue.use(VueSanitize);

// Install BootstrapVue
Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

// global mixin
if (RoleMixin) Vue.mixin(RoleMixin);
if (UtilsMixin) Vue.mixin(UtilsMixin);

Vue.config.productionTip = false;

// If a deployment occurs while a user is logged in
// and chunk references are stored in memory, this will happen
// because it's likely the chunk hashes will have changed
router.onError((error) => {
  if (error.name === 'ChunkLoadError') {
    window.location = '/';
  }
});

if (process.env.VUE_APP_SENTRY_ENABLED.toString() === 'true') {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [
          'localhost',
          'api.intdev.performa-labs.net',
          'api.qa.performa-labs.net',
          'api.performa-labs.net',
          /^\//,
        ],
        shouldCreateSpanForRequest: shouldCreateHybridCoursesSpanForRequest,
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,
    release: process.env.VUE_APP_BUILD_VERSION,
    logErrors: process.env.VUE_APP_ENVIRONMENT.toString() !== 'Production',
    initialScope: {},
    // Endpoint-targeted redaction only; nonmatching events pass unchanged.
    beforeBreadcrumb: sanitizeHybridCoursesBreadcrumb,
    beforeSend: sanitizeHybridCoursesSentryEvent,
    ...(process.env.VUE_APP_SENTRY_ENVIRONMENT.toString() !== 'Local'
      ? { allowUrls: ['https://' + process.env.VUE_APP_ENVIRONMENT_HOST] }
      : {}),
  });
}

// Handle this all before initializing Vue to speed up login/re-directions
(async function () {
  // Check if API is online first
  try {
    await userAPI.getOfflineStatus();
  } catch (e) {
    console.log(e);
  }

  try {
    let user = null;

    // Check if unplanned downtime before initializing everything
    // OK to do this async. We don't want to unnecessarily delay initialization
    // and if the user isn't already logged in, then they will be redirected
    // to planned downtime page once they get to the API login

    // If being redirected back from identity server
    if (window.location.pathname === '/login') {
      user = await userManager.signinCallback();
      history.replaceState(null, '', '/');
    } else if (window.location.pathname === '/silent') {
      await userManager.signinSilentCallback();
    } else {
      user = await userManager.getUser();
      if (!user) {
        // Preserve route for post login
        let state = {};
        if (
          window.location.pathname !== '/silent' &&
          window.location.pathname !== '/login' &&
          window.location.pathname !== '' &&
          window.location.pathname !== '/'
        ) {
          state = {
            postLoginRoute:
              window.location.pathname +
              window.location.search +
              window.location.hash,
          };
        }
        await userManager.signinRedirect({ state });
      }
    }

    if (user) {
      setUser(user);

      const token = user.access_token;
      const parsedToken = parseJwt(token);

      window.gtag('config', process.env.VUE_APP_GA4_ID, {
        user_id: parsedToken.sub,
        user_properties: {
          org_id: parsedToken.orgId,
          role: RoleMixin.methods.$getHighestPrivilegedRole(parsedToken.rol),
        },
      });

      // Modify Sentry Scope with user's info
      Sentry.configureScope((scope) => {
        scope.setTags({
          'group-id': parsedToken.groupId,
          'org-id': parsedToken.orgId,
          role: RoleMixin.methods.$getHighestPrivilegedRole(parsedToken.rol),
          timezone: parsedToken.zoneinfo,
        });
        scope.setUser({
          id: parsedToken.sub,
          email: parsedToken.email,
        });
      });

      if (window.localStorage && window.localStorage.getItem('performa-fl')) {
        window.localStorage.removeItem('performa-fl');
        if (
          process.env.VUE_APP_SENTRY_LOG_LOGIN_AFTER_FAILURE.toString() ===
          'true'
        ) {
          Sentry.captureMessage('Successful login after failed attempt');
        }
      }

      // Initialize the App
      new Vue({
        router,
        store,
        render: (h) => h(App),
      }).$mount('#app');
    }
  } catch (e) {
    console.log(e);
    Sentry.withScope(function (scope) {
      const multipleFailedLogins =
        window.localStorage && window.localStorage.getItem('performa-fl');
      if (multipleFailedLogins) {
        // If we've failed logging in once before, then set level to fatal
        scope.setLevel(Sentry.Severity.Fatal);
      }
      Sentry.captureException(
        multipleFailedLogins
          ? new Error('Sequential Failed Logins: ' + e.message)
          : e
      );
    });
    // If anything login related fails, force the user to log back in.
    // The exception is if the user's session state was not found. One
    // cause of this is users that have bookmarked the API's login page
    // with the redirection query string containing expired information.
    // In this case they have logged in already, but passed invalid info
    // to the client site. We will redirect them back to the API to but
    // won't force login
    const userStateMismatch =
      e.message === 'No matching state found in storage';

    if (userStateMismatch) {
      // Store a flag in localStorage that there was a failed login
      try {
        if (window.localStorage) {
          window.localStorage.setItem('performa-fl', true);
        }
      } catch (e) {
        console.log(e);
      }
    }
    userManager.signinRedirect(userStateMismatch ? {} : { prompt: 'login' });
  }
})();
